/**
 * WriterSpotlight.jsx — Discover-page banner introducing the "Writer of
 * the Week". Sits ABOVE the Pick-of-the-Week hero carousel.
 *
 * Pulls /api/writer-spotlight (deterministic per ISO week, 6 h cache).
 * Renders a glass-card with avatar, name, niche tagline, and a quick
 * stat strip (books · followers · genre variety · avg rating).
 *
 * Hides itself silently when no eligible writer exists yet (e.g. brand-
 * new platform with zero published uploads) so first-time users don't
 * see an empty box.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Star, Users, Library, Palette } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const Stat = ({ icon: Icon, label, value, accent = 'text-indigo-200' }) => (
  <div className="flex items-center gap-1.5 min-w-0">
    <Icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${accent} shrink-0`} />
    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400 shrink-0">
      {label}
    </span>
    <span className="text-xs sm:text-sm font-black text-white tabular-nums truncate">
      {value}
    </span>
  </div>
);

const WriterSpotlight = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/writer-spotlight`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.writer) setData(j);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [BACKEND_URL]);

  if (!data?.writer) return null;
  const { writer, stats, niche } = data;

  const initial = (writer.name || '?').trim().charAt(0).toUpperCase();
  const ratingValue =
    stats.avg_rating > 0 ? stats.avg_rating.toFixed(1) : '—';

  return (
    <section
      className="px-4 sm:px-6 mb-4"
      data-testid="writer-spotlight"
    >
      <button
        type="button"
        onClick={() => {
          // Always route to the library filtered by the scribe's name —
          // the /writer/<user_id> profile page doesn't exist yet, so the
          // safest tap target is the existing author filter. (Bug fix
          // Feb 26 2026: previous code tried /writer/<user_id> first,
          // which produced a blank screen since no route handles it.)
          navigate(`/library?author=${encodeURIComponent(writer.pen_name || writer.name)}`);
        }}
        className="group w-full text-left relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-rose-500/10 hover:from-amber-500/25 hover:via-orange-500/20 hover:to-rose-500/20 transition-colors p-3 sm:p-4 shadow-lg shadow-black/30"
        aria-label={`Scribe of the week: ${writer.name}`}
      >
        {/* Crown badge — anchored top-right */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-400 text-slate-900 text-[9px] sm:text-[10px] font-black uppercase tracking-widest shadow-lg">
          <Crown className="w-3 h-3" />
          Scribe of the Week
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          {/* Avatar */}
          <div className="relative shrink-0">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center ring-2 ring-amber-300/60 shadow-xl">
              {writer.avatar_url ? (
                <img src={writer.avatar_url} alt={writer.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-xl sm:text-2xl font-black">{initial}</span>
              )}
            </div>
          </div>

          {/* Name + niche blurb */}
          <div className="min-w-0 flex-1 pr-20 sm:pr-24">
            <h2
              className="text-base sm:text-lg font-black text-white truncate group-hover:text-amber-100 transition-colors"
              data-testid="writer-spotlight-name"
            >
              {writer.name}
            </h2>
            <p
              className="text-[11px] sm:text-xs text-slate-200/85 mt-0.5 line-clamp-2 leading-snug"
              data-testid="writer-spotlight-blurb"
            >
              {niche.blurb}
            </p>
          </div>
        </div>

        {/* Stat strip */}
        <div
          className="mt-3 sm:mt-3.5 flex items-center gap-3 sm:gap-4 flex-wrap"
          data-testid="writer-spotlight-stats"
        >
          <Stat icon={Library} label="Books" value={stats.published} accent="text-indigo-300" />
          <Stat icon={Users} label="Followers" value={stats.followers} accent="text-emerald-300" />
          <Stat icon={Palette} label="Genres" value={stats.genre_variety_count} accent="text-fuchsia-300" />
          <Stat icon={Star} label="Rating" value={ratingValue} accent="text-amber-300" />
        </div>
      </button>
    </section>
  );
};

export default WriterSpotlight;
